// src/components/layout/Layout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import MobileNav from './MobileNav';
import SofiaFab from '../companion/SofiaFab';
import NativeTabBar from './NativeTabBar';
import { Capacitor } from '@capacitor/core';
import { useApp } from '../../context/AppContext';

// Per-route document titles (WCAG 2.4.2). Detail pages sharing a segment get
// the section name; the content itself is announced by the page's h1.
const ROUTE_TITLES = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/assessment': 'Philosophical Profile Assessment',
  '/modules': 'Learning Modules',
  '/toolkit': "The Philosopher's Toolkit",
  '/companion': 'Sofia, Philosophy Companion',
  '/journal': "The Philosopher's Journal",
  '/positions': 'Your Positions',
  '/lineage': 'The Lineage',
  '/resources': 'Philosophy Resources',
  '/privacy': 'Data & Privacy',
  '/terms': 'Terms of Use',
  '/profile': 'Your Profile',
  '/support': 'Support',
};

// Inside the iOS app a tab bar replaces the drawer, and Sofia has her own tab, so
// neither the floating Sofia button nor scroll-to-top is needed there (iOS scrolls
// to the top when you tap the status bar).
const IS_NATIVE = Capacitor.isNativePlatform();

const Layout = () => {
  const location = useLocation();
  const { notifications } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu on route change
    setMobileMenuOpen(false);
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const section = ROUTE_TITLES[location.pathname]
      || ROUTE_TITLES['/' + location.pathname.split('/')[1]];
    document.title = section
      ? `${section} | Profound Philosophy`
      : 'Profound Philosophy';
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Keyboard users land here first; the link is visible only while focussed */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-slate-900 focus:text-white focus:border focus:border-amber-500"
      >
        Skip to main content
      </a>
      {/* Background Pattern */}
      <div className="fixed inset-0 opacity-10 pointer-events-none">
        <div className="absolute inset-0" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Header */}
      <Header 
        scrolled={scrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileNav onClose={() => setMobileMenuOpen(false)} />
        )}
      </AnimatePresence>

      {/* Notifications: role="status" so messages reach screen readers */}
      <AnimatePresence>
        {notifications.length > 0 && (
          <div className="fixed top-20 right-4 z-50 space-y-2" role="status" aria-live="polite">
            {notifications.map(notification => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                className={`p-4 rounded-lg shadow-lg backdrop-blur-md ${
                  notification.type === 'success' ? 'bg-green-500/20 border border-green-500/30' :
                  notification.type === 'error' ? 'bg-red-500/20 border border-red-500/30' :
                  'bg-blue-500/20 border border-blue-500/30'
                }`}
              >
                <p className="text-white text-sm">{notification.message}</p>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Main Content. No z-index here on purpose: a z-index makes <main> a stacking
          context, which trapped every page's modal (certificate, celebration, thinker
          drawer, game dialogs) underneath the fixed header and the iOS tab bar, so the
          certificate's close button sat hidden under the header. Being positioned and
          later in the DOM already keeps it above the fixed background pattern. */}
      <main id="main-content" tabIndex={-1} className="relative outline-none">
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />

      {/* Safe area on mobile: lets the last lines of any page scroll clear of
          the floating Sofia/scroll-to-top row instead of sitting under it. */}
      {IS_NATIVE ? (
        <div className="h-[calc(4rem+env(safe-area-inset-bottom))]" aria-hidden="true" />
      ) : (
        <div className="h-20 sm:hidden" aria-hidden="true" />
      )}

      {/* Sofia: floating companion entry, fleet pattern */}
      {!IS_NATIVE && <SofiaFab />}

      {IS_NATIVE && <NativeTabBar />}

      {/* Scroll to Top Button: on mobile it sits beside Sofia on one row, so the
          pair covers a single band of text instead of a tall column of it;
          on desktop it stacks above her. Hidden on /companion: there it floated
          directly over the chat's input bar and read as the send button (Nigel
          pressed it mid-message on mobile, 2026-09-03). */}
      <AnimatePresence>
        {scrolled && !IS_NATIVE && !location.pathname.startsWith('/companion') && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-20 sm:bottom-24 sm:right-6 p-3 bg-conversation-blue hover:bg-conversation-blue/80 rounded-full shadow-lg z-40 transition-colors"
            aria-label="Scroll to top"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;